import { createRouter, createWebHistory } from 'vue-router'
import store from "../store/index.js"
import { postToParent } from "../iframeParent.js"

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import("../Pages/Home.vue")
  },

  {
    path: '/sso/login',
    name: 'sso-login',
    component: () => import("../Pages/Sso/login.vue")
  },

  // {
  //   path: '/home',
  //   name: 'main-home',
  //   component: () => import("../Pages/Home.vue")
  // },

  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import("../Pages/Auth/ResetPassword.vue")
  },


  {
    path: '/news',
    name: 'news.index',
    component: () => import("../Pages/News/Index.vue")
  },
  {
    path: '/news/:id',
    name: 'news.show',
    component: () => import("../Pages/News/Show.vue")
  },
  {
    path: '/rooms',
    name: 'rooms',
    component: () => import("../Pages/Rooms/Index.vue")
  },
  {
    path: '/rooms/:id',
    name: 'rooms.show',
    component: () => import("../Pages/Rooms/Show.vue")
  },
  {
    path: '/profile/settings',
    name: 'profile.settings',
    component: () => import("../Pages/Profile/Settings.vue")
  },
  {
    path: '/profile/services',
    name: 'profile.services',
    component: () => import("../Pages/Profile/Services/Index.vue")
  },
  {
    path: '/profile/services/create',
    name: 'profile.services.create',
    component: () => import("../Pages/Profile/Services/Create.vue")
  },
  {
    path: '/profile/services/:id/edit',
    name: 'profile.services.edit',
    component: () => import("../Pages/Profile/Services/Edit.vue")
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import("../Pages/Profile/Profile.vue")
  },
  {
    path: '/experts',
    name: 'experts.index',
    component: () => import("../Pages/Experts/Index.vue")
  },
  {
    path: '/experts/:id',
    name: 'experts.show',
    component: () => import("../Pages/Experts/Show.vue")
  },

  {
    path: '/experts/:id/services',
    name: 'experts.services.index',
    component: () => import("../Pages/Experts/Services/Index.vue")
  },

  {
    path: '/404',
    name: '404',
    component: () => import("../Pages/404.vue")
  },

  {
    path: "/:catchAll(.*)",
    component: () => import("../Pages/404.vue")
  },
  {
    path: '/messages',
    name: 'messages.index',
    component: () => import("../Pages/Messages/Index.vue")
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL || '/'),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
  routes
})
const parseJwtPayload = (token) => {
  if(!token || typeof token !== 'string'){
    return null
  }
  const parts = token.split('.')
  if(parts.length < 2){
    return null
  }
  const payload = parts[1]
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=')
  try{
    return JSON.parse(atob(normalized))
  }catch (e){
    return null
  }
}

const getTokenExpiryFromJwt = (token) => {
  const payload = parseJwtPayload(token)
  const exp = Number(payload && payload.exp ? payload.exp : 0)
  if(!Number.isFinite(exp) || exp <= 0){
    return 0
  }
  return exp * 1000
}

const resolveTokenExpiryTimestamp = (token, expiresInRaw) => {
  const jwtExpiry = getTokenExpiryFromJwt(token)
  if(jwtExpiry > 0){
    return jwtExpiry
  }
  const now = Date.now()

  const expiresIn = Number(expiresInRaw)
  if(Number.isFinite(expiresIn) && expiresIn > 0){
    const seconds = expiresIn > 1000000 ? expiresIn : (expiresIn * 60)
    return now + (seconds * 1000)
  }

  return now + (24 * 60 * 60 * 1000)
}

const bootstrapTokenFromRouteQuery = (to) => {
  const queryToken = typeof to?.query?.token === 'string' ? to.query.token.trim() : ''
  if(!queryToken){
    return false
  }

  const previousToken = localStorage.getItem('x_xsrf_token')
  if(previousToken && previousToken !== queryToken){
    localStorage.removeItem('user_info')
    store.commit('auth/clearUser')
  }

  const expiryTimestamp = resolveTokenExpiryTimestamp(queryToken, to.query.expires_in)
  localStorage.setItem('x_xsrf_token', queryToken)
  localStorage.setItem('x_xsrf_token_time', new Date(expiryTimestamp).toISOString())

  return true
}

const buildRouteWithoutAuthQuery = (to) => {
  const query = { ...to.query }
  let changed = false
  if(Object.prototype.hasOwnProperty.call(query, 'token')){
    delete query.token
    changed = true
  }
  if(Object.prototype.hasOwnProperty.call(query, 'expires_in')){
    delete query.expires_in
    changed = true
  }
  if(!changed){
    return null
  }
  return {
    path: to.path,
    query: query,
    hash: to.hash,
    replace: true
  }
}

const isTokenExpired = () => {
  const token = localStorage.getItem('x_xsrf_token')
  if(!token){
    return false
  }
  const jwtExpiry = getTokenExpiryFromJwt(token)
  if(jwtExpiry > 0){
    return jwtExpiry <= Date.now()
  }
  const raw = localStorage.getItem('x_xsrf_token_time')
  if(raw){
    const time = new Date(raw).getTime()
    if(Number.isFinite(time)){
      return time <= Date.now()
    }
  }

  return false
}

const clearSession = () => {
  localStorage.removeItem('x_xsrf_token')
  localStorage.removeItem('x_xsrf_token_time')
  localStorage.removeItem('user_info')
  store.commit('auth/clearUser')
}
const isSsoLoginRoute = (to) => to && to.name === 'sso-login'

router.beforeEach(async (to, from, next) => {
  if(!isSsoLoginRoute(to)){
    const tokenBootstrapped = bootstrapTokenFromRouteQuery(to)
    if(tokenBootstrapped){
      await store.dispatch('auth/bootstrapUserWithRetry', { attempts: 4, delayMs: 250 })
      const cleanRoute = buildRouteWithoutAuthQuery(to)
      if(cleanRoute){
        return next(cleanRoute)
      }
    }
    const token = localStorage.getItem('x_xsrf_token')
    const tokenExpired = token && isTokenExpired()
    if(tokenExpired){
      clearSession()
    }
    let hasToken = !!token && !tokenExpired
    if(hasToken && !store.state.auth.isAuth){
      await store.dispatch('auth/bootstrapUserWithRetry', { attempts: 3, delayMs: 250 })
      const refreshedToken = localStorage.getItem('x_xsrf_token')
      const refreshedTokenExpired = refreshedToken && isTokenExpired()
      if(refreshedTokenExpired){
        clearSession()
      }
      hasToken = !!refreshedToken && !refreshedTokenExpired
    }

    if(!hasToken){
      const authRoutes = [
        'profile',
        'profile.settings',
        'profile.services',
        'profile.services.edit',
        'profile.services.create',
        'messages.index',
      ]

      if(authRoutes.includes(to.name)){
        return next({name: 'home'})
      }
    }
  }

  // if((to.name === 'user.login' || to.name === 'user.registration') && token){
  //   // return next({
  //   //   name: 'user.personal'
  //   // })
  // }
  document.body.overflow = ''

  next()

  setTimeout(()=>{
    const main = document.querySelector('#app > main')
    if(main){
      var height = main.scrollHeight; // Получение высоты содержимого
      postToParent({ height: height });
    }
  }, 1000)
})

export default router
